import React from 'react';
import { Card, Button, Badge } from '../ui';
import { User, MessageCircle, Heart, AlertCircle } from 'lucide-react';
import { SeedingProfile } from '../../types';

interface Demographics {
  seeding_profile?: SeedingProfile;
}

interface DemographicsPanelProps {
  accountId: string;
  demographics?: Demographics;
  onUpdate: () => void;
}

export const DemographicsPanel: React.FC<DemographicsPanelProps> = ({
  accountId,
  demographics,
  onUpdate
}) => {
  const seedingProfile = demographics?.seeding_profile;

  if (!seedingProfile) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center text-center py-8">
          <AlertCircle className="text-yellow-500 mb-3" size={48} />
          <h3 className="text-lg font-semibold text-tg-text mb-2">
            No Seeding Profile
          </h3>
          <p className="text-tg-hint mb-4">
            This account doesn't have a seeding profile configured yet.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Role & Identity */}
      <Card className="!border-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="text-tg-link" size={20} />
            </div>
            <h3 className="font-semibold text-tg-text">Role & Identity</h3>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-tg-hint">Role</p>
            <Badge variant="success" className="text-sm">
              {seedingProfile.role?.role_name}
            </Badge>
          </div>
          <div>
            <p className="text-xs text-tg-hint">Age</p>
            <p className="text-sm font-medium text-tg-text">{seedingProfile.age} years</p>
          </div>
          <div>
            <p className="text-xs text-tg-hint">Gender</p>
            <Badge variant="info">
              {seedingProfile.gender === 'male' ? 'Male' : 
               seedingProfile.gender === 'female' ? 'Female' : 'Other'}
            </Badge>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-tg-hint">Location</p>
            <p className="text-sm font-medium text-tg-text">{seedingProfile.location}</p>
          </div>
        </div>
      </Card>

      {/* Conversation Style */}
      {seedingProfile.conversation_style && (
        <Card className="!border-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageCircle className="text-purple-600" size={20} />
              </div>
              <h3 className="font-semibold text-tg-text">Conversation Style</h3>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-tg-hint">Tone</p>
              <Badge variant="success">
                {seedingProfile.conversation_style.tone}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-tg-hint">Emoji Usage</p>
              <Badge variant="info">
                {seedingProfile.conversation_style.emoji_usage}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-tg-hint">Response Length</p>
              <Badge>
                {seedingProfile.conversation_style.response_length}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-tg-hint">Vocabulary</p>
              <Badge>
                {seedingProfile.conversation_style.vocabulary}
              </Badge>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-tg-hint">Forms of Address</p>
              <Badge variant="info">
                {seedingProfile.conversation_style.forms_of_address}
              </Badge>
            </div>
          </div>
        </Card>
      )}

      {/* Interests */}
      {seedingProfile.interests && seedingProfile.interests.length > 0 && (
        <Card className="!border-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Heart className="text-pink-600" size={20} />
              </div>
              <h3 className="font-semibold text-tg-text">Interests</h3>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {seedingProfile.interests.map((interest, idx) => (
              <Badge key={idx} variant="success">{interest}</Badge>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
